import React, { useState } from "react";
import { Button } from "../components/Button.jsx";

export function FourTaskPilotInstructions({ next }) {
  const [answers, setAnswers] = useState({
    questionCount: "",
    outsideHelp: "",
    confidenceMeaning: "",
    bonusPayment: "",
  });
  const [showError, setShowError] = useState(false);

  const correctAnswers = {
    questionCount: "4",
    outsideHelp: "no",
    confidenceMeaning: "probability",
    bonusPayment: "per-question",
  };

  function handleAnswerChange(key, value) {
    setAnswers((previous) => ({ ...previous, [key]: value }));
    setShowError(false);
  }

  function handleNext() {
    const allCorrect = Object.entries(correctAnswers).every(
      ([key, value]) => answers[key] === value
    );

    if (!allCorrect) {
      setShowError(true);
      return;
    }

    next();
  }

  return (
    <div className="mt-3 sm:mt-5 p-20">
      <h3 className="text-lg leading-6 font-medium text-gray-900">
        Instructions
      </h3>

      <div className="mt-3 mb-6 space-y-6 text-gray-800">
        <div className="bg-white rounded-md shadow-sm border border-gray-200 p-4">
          <h4 className="font-semibold text-gray-900 mb-3 text-lg">
            1. Objective
          </h4>
          <p>
            You will answer <strong>4 standalone prediction questions</strong>.
          </p>
        </div>

        <div className="bg-white rounded-md shadow-sm border border-gray-200 p-4">
          <h4 className="font-semibold text-gray-900 mb-3 text-lg">
            2. What You Need To Do
          </h4>
          <div className="space-y-2">
            <p>
              <strong>Step 1:</strong> Read each question carefully and answer
              it on your own.
            </p>
            <p>
              <strong>Step 2:</strong> For each question, enter your{" "}
              <strong>confidence</strong>. Here, confidence means{" "}
              <strong>the probability that your chosen answer is correct</strong>.
            </p>
            <p>
              <strong>Step 3:</strong> After all 4 questions, you will complete
              a short exit survey.
            </p>
          </div>
        </div>

        <div className="bg-blue-50 rounded-md shadow-sm border border-blue-200 p-4">
          <h4 className="font-semibold text-blue-900 mb-3 text-lg">
            3. Payment
          </h4>
          <div className="space-y-2 text-blue-900">
            <p>
              <strong>Base Payment:</strong> You will receive <strong>$1.5</strong>.
            </p>
            <p>
              <strong>Accuracy Bonus:</strong> Each correct answer earns{" "}
              <strong>$0.5</strong>.
            </p>
            <p>
              Because there are 4 questions, the maximum bonus is{" "}
              <strong>$2</strong>.
            </p>
          </div>
        </div>

        <div className="bg-amber-50 rounded-md shadow-sm border border-amber-200 p-4">
          <h4 className="font-semibold text-amber-900 mb-3 text-lg">
            4. Confidence Rules
          </h4>
          <div className="space-y-2 text-amber-900">
            <p>
              For open-ended questions, confidence must be a whole number from{" "}
              <strong>0</strong> to <strong>100</strong>.
            </p>
            <p>
              For questions with two answer options, confidence must be a whole
              number from <strong>50</strong> to <strong>100</strong>.
            </p>
            <p>
              For questions with three answer options, confidence must be a
              whole number from <strong>33</strong> to <strong>100</strong>.
            </p>
            <p>
              Values below those lower bounds are not allowed because they would
              imply that an unchosen option is more likely to be correct than
              the answer you selected.
            </p>
          </div>
        </div>

        <div className="bg-red-50 rounded-md shadow-sm border border-red-200 p-4">
          <h4 className="font-semibold text-red-900 mb-3 text-lg">
            5. Research Integrity
          </h4>
          <div className="space-y-2 text-red-900">
            <p>
              This task is part of an experiment. For the experiment to be
              successful, please <strong>work on your own</strong>.
            </p>
            <p>
              Do <strong>not</strong> use AI systems, LLMs, or any other outside
              help while answering the questions.
            </p>
            <p>
              Copying and pasting is disabled during the task.
            </p>
            <p>
              We use checks to detect likely AI-generated responses, and we may
              reject responses that appear to rely on prohibited tool use.
            </p>
          </div>
        </div>

        <div className="bg-yellow-50 rounded-md shadow-sm border border-yellow-200 p-4">
          <h4 className="font-semibold text-yellow-900 mb-3 text-lg">
            Please answer the following questions to proceed:
          </h4>

          <div className="space-y-5">
            <div>
              <p className="font-medium text-gray-900 mb-2">
                1. How many prediction questions will you answer?
              </p>
              <div className="space-y-1 ml-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="questionCount"
                    value="2"
                    checked={answers.questionCount === "2"}
                    onChange={(event) =>
                      handleAnswerChange("questionCount", event.target.value)
                    }
                    className="mr-2"
                  />
                  <span>2</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="questionCount"
                    value="4"
                    checked={answers.questionCount === "4"}
                    onChange={(event) =>
                      handleAnswerChange("questionCount", event.target.value)
                    }
                    className="mr-2"
                  />
                  <span>4</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="questionCount"
                    value="6"
                    checked={answers.questionCount === "6"}
                    onChange={(event) =>
                      handleAnswerChange("questionCount", event.target.value)
                    }
                    className="mr-2"
                  />
                  <span>6</span>
                </label>
              </div>
            </div>

            <div>
              <p className="font-medium text-gray-900 mb-2">
                2. Should you use AI or any other outside help while answering
                the questions?
              </p>
              <div className="space-y-1 ml-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="outsideHelp"
                    value="yes"
                    checked={answers.outsideHelp === "yes"}
                    onChange={(event) =>
                      handleAnswerChange("outsideHelp", event.target.value)
                    }
                    className="mr-2"
                  />
                  <span>Yes</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="outsideHelp"
                    value="no"
                    checked={answers.outsideHelp === "no"}
                    onChange={(event) =>
                      handleAnswerChange("outsideHelp", event.target.value)
                    }
                    className="mr-2"
                  />
                  <span>No</span>
                </label>
              </div>
            </div>

            <div>
              <p className="font-medium text-gray-900 mb-2">
                3. In this task, what does confidence mean?
              </p>
              <div className="space-y-1 ml-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="confidenceMeaning"
                    value="certainty"
                    checked={answers.confidenceMeaning === "certainty"}
                    onChange={(event) =>
                      handleAnswerChange("confidenceMeaning", event.target.value)
                    }
                    className="mr-2"
                  />
                  <span>How strongly you feel about the question in general</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="confidenceMeaning"
                    value="probability"
                    checked={answers.confidenceMeaning === "probability"}
                    onChange={(event) =>
                      handleAnswerChange("confidenceMeaning", event.target.value)
                    }
                    className="mr-2"
                  />
                  <span>The probability that your chosen answer is correct</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="confidenceMeaning"
                    value="speed"
                    checked={answers.confidenceMeaning === "speed"}
                    onChange={(event) =>
                      handleAnswerChange("confidenceMeaning", event.target.value)
                    }
                    className="mr-2"
                  />
                  <span>How quickly you reached your answer</span>
                </label>
              </div>
            </div>

            <div>
              <p className="font-medium text-gray-900 mb-2">
                4. How is the bonus payment calculated?
              </p>
              <div className="space-y-1 ml-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="bonusPayment"
                    value="flat"
                    checked={answers.bonusPayment === "flat"}
                    onChange={(event) =>
                      handleAnswerChange("bonusPayment", event.target.value)
                    }
                    className="mr-2"
                  />
                  <span>You receive a flat $2 bonus no matter how many answers are correct</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="bonusPayment"
                    value="per-question"
                    checked={answers.bonusPayment === "per-question"}
                    onChange={(event) =>
                      handleAnswerChange("bonusPayment", event.target.value)
                    }
                    className="mr-2"
                  />
                  <span>Each correct answer earns $0.5, up to a maximum $2 bonus</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="bonusPayment"
                    value="none"
                    checked={answers.bonusPayment === "none"}
                    onChange={(event) =>
                      handleAnswerChange("bonusPayment", event.target.value)
                    }
                    className="mr-2"
                  />
                  <span>There is no bonus payment</span>
                </label>
              </div>
            </div>
          </div>

          {showError && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-red-800 text-sm">
                Please answer all questions correctly before proceeding.
              </p>
            </div>
          )}
        </div>
      </div>

      <Button handleClick={handleNext} autoFocus>
        <p>Next</p>
      </Button>
    </div>
  );
}
